import {
  faBuilding,
  faCalendar,
  faClock
} from '@fortawesome/free-regular-svg-icons';
import {
  faArrowAltCircleDown,
  faCheck,
  faPen,
  faPlus,
  faSearch,
  faTag,
  faTimes,
  faUserCircle
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import EmptyList from 'components/common/EmptyList';
import FalconComponentCard from 'components/common/FalconComponentCard';
import Flex from 'components/common/Flex';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import CustomerImportModal from 'components/modals/CustomerImportModal';
import CustomerRegistrationOffcanvas from 'components/offcanvas/CustomerRegistrationOffcanvas';
import Pagination from 'components/pagination';
import useApi from 'helpers/api';
import { useDebounce } from 'helpers/utils';
import useAdvanceTable from 'hooks/useAdvanceTable';
import { useFormatDateTime } from 'hooks/useFormatDateTime';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { useEffect, useState } from 'react';
import { Badge, Button, Col, Form, FormControl, Row } from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import { Link } from 'react-router-dom';

const CustomerRegistration = () => {
  const api = useApi();
  const formatDateTime = useFormatDateTime();
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showModalImport, setShowModalImport] = useState(false);

  const [customers, setCustomers] = useState({
    content: [],
    totalElements: 0
  });
  const [filters, setFilters] = useState({
    page: 0,
    size: 20,
    includeInactives: false,
    name: ''
  });
  const [filterNameCustomer, setFilterNameCustomer] = useState('');

  const getData = async () => {
    setIsLoading(true);
    const queryParams = new URLSearchParams(filters).toString();
    const response = await api.customers.list(queryParams);
    if (response.content) setCustomers(response);
    setIsLoading(false);
  };

  useEffect(() => {
    getData();
  }, [filters]);

  const handleSearchChange = e => {
    const searchValue = e.target.value;
    setFilterNameCustomer(searchValue);
  };

  const debouncedSearch = useDebounce(searchValue => {
    setFilters(prevFilters => ({
      ...prevFilters,
      page: 0,
      name: searchValue
    }));
  }, 400);

  // Efeito para executar a busca com debounce quando o valor do filtro mudar
  useEffect(() => {
    debouncedSearch(filterNameCustomer);
  }, [filterNameCustomer]);

  const columns = [
    {
      accessorKey: 'name',
      header: 'Nome',
      meta: {
        headerProps: { className: 'text-900 w-50' }
      },
      cell: rowData => {
        // Desestruture os novos campos aqui
        const {
          id,
          type,
          fantasyName,
          name,
          cpf,
          cnpj,
          tags = []
        } = rowData.row.original;

        return (
          // Usar um <div> como container principal para o conteúdo da célula
          <div>
            <Flex alignItems="center" className="mb-2">
              <FontAwesomeIcon
                icon={type === 'JURIDICAL_PERSON' ? faBuilding : faUserCircle}
                className="me-2 text-primary"
                size="lg"
              />
              <Link to={`/registration/customers/${id}`} className="fw-bold">
                {type === 'JURIDICAL_PERSON' ? fantasyName : name}
              </Link>
            </Flex>

            {/* Subtítulo com tipo de pessoa e documento */}
            <div className="fs-10 text-muted">
              {cpf && <span>{cpf}</span>}
              {cnpj && <span>{cnpj}</span>}
            </div>
          </div>
        );
      }
    },
    {
      accessorKey: 'tags',
      header: 'Tags',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { tags } = rowData.row.original;
        return (
          <Flex
            alignItems="center"
            className="fs-11 text-center"
            style={{ maxWidth: '200px' }}
            wrap="wrap"
          >
            <FontAwesomeIcon icon={faTag} className="me-2" />
            {tags && tags.length > 0
              ? tags.map(tag => (
                  <Badge
                    bg="light"
                    text="dark"
                    className="text-center me-1 mb-1 p-2"
                    style={{ fontSize: '12px' }}
                    key={tag.id}
                  >
                    {tag.name}
                  </Badge>
                ))
              : '-'}
          </Flex>
        );
      }
    },
    {
      accessorKey: 'creationDate',
      header: 'Data de criação',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { creationDate } = rowData.row.original;
        return (
          <Flex alignItems="center">
            <FontAwesomeIcon icon={faCalendar} className="me-1" />
            <span>{formatDateTime(creationDate)}</span>
          </Flex>
        );
      }
    },
    {
      accessorKey: 'lastUpdateDate',
      header: 'Última atualização',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { lastUpdateDate } = rowData.row.original;
        return (
          <Flex alignItems="center">
            <FontAwesomeIcon icon={faClock} className="me-1" />
            <span>{formatDateTime(lastUpdateDate)}</span>
          </Flex>
        );
      }
    },
    {
      accessorKey: 'status',
      header: 'Ativo',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { inactive } = rowData.row.original;
        return inactive ? (
          <FontAwesomeIcon icon={faTimes} className="text-danger" />
        ) : (
          <FontAwesomeIcon icon={faCheck} className="text-success" />
        );
      }
    },
    {
      accessorKey: 'actions',
      header: 'Ações',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const item = rowData.row.original;
        return (
          <Link
            className="btn btn-link btn-sm"
            to={`/registration/customers/${item.id}`}
          >
            <FontAwesomeIcon icon={faPen} size="sm" />
          </Link>
        );
      }
    }
  ];

  const table = useAdvanceTable({
    data: customers.content,
    columns
  });

  const handleCloseModalRegister = () => {
    setShowModal(false);
  };

  const isEmpty = !isLoading && customers.content.length === 0;

  return (
    <>
      <CustomerImportModal
        show={showModalImport}
        handleClose={() => setShowModalImport(false)}
        refresh={() => getData()}
      />

      <FalconComponentCard>
        <FalconComponentCard.Header title="Clientes">
          <Flex>
            <Button
              size="sm"
              className="me-2"
              onClick={() => setShowModalImport(true)}
            >
              <FontAwesomeIcon
                icon={faArrowAltCircleDown}
                size="sm"
                className="me-2"
              />
              Importar
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => setShowModal(true)}
            >
              <FontAwesomeIcon icon={faPlus} size="sm" className="me-2" />
              Novo
            </Button>
          </Flex>
        </FalconComponentCard.Header>
        <FalconComponentCard.Body className="bg-white">
          <CustomerRegistrationOffcanvas
            createdCustomer={item => {}}
            showModal={showModal}
            isSuccess={response => {
              if (response) getData();
            }}
            handleClose={() => handleCloseModalRegister()}
          />
          <Row className="justify-content-between g-0">
            <Col className="d-flex align-items-center col-auto pe-3 mb-3">
              <div className="position-relative input-search-width">
                <FormControl
                  size="sm"
                  id="search"
                  type="search"
                  placeholder="Buscar..."
                  value={filterNameCustomer}
                  onChange={handleSearchChange}
                  className="rounded-pill ps-4"
                />
                <FontAwesomeIcon
                  icon={faSearch}
                  className="position-absolute text-secondary"
                  style={{
                    left: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: '12px'
                  }}
                />
              </div>
            </Col>

            <Col className="col-auto px-3">
              <Form.Select
                size="sm"
                className="mb-3 rounded-pill"
                onChange={e =>
                  setFilters(prevFilters => ({
                    ...prevFilters,
                    includeInactives: e.target.value
                  }))
                }
              >
                <option value="true">Filtro por status</option>
                <option value="true">Todos</option>
                <option value="false">Somente ativos</option>
              </Form.Select>
            </Col>
          </Row>
          {isLoading ? (
            <Skeleton count={10} height={44} baseColor="#5E6E82" />
          ) : isEmpty ? (
            <EmptyList imgSize={500} vhSize={75} />
          ) : (
            <>
              <AdvanceTableProvider {...table}>
                <AdvanceTable
                  table
                  headerClassName="text-nowrap align-middle"
                  rowClassName="align-middle white-space-nowrap"
                  tableProps={{
                    striped: true,
                    className: 'fs-10 mb-0 overflow-hidden'
                  }}
                />
              </AdvanceTableProvider>
              <Pagination
                data={customers}
                filters={filters}
                setFilters={setFilters}
              />
            </>
          )}
        </FalconComponentCard.Body>
      </FalconComponentCard>
    </>
  );
};

export default CustomerRegistration;
